import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, effect, inject, input, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FilterSelection } from 'src/app/components/filters/filters.component';
import { Announcement, AnnouncementBarPage } from 'src/app/core/interfaces/store-configuration.interface';
import { CategorySection } from '../../models/product.model';
import { FilterValue, ProductFilterType } from '../../services/filters-adapter.service';

import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AnnouncementBarComponent } from "src/app/components/announcement-bar/announcement-bar.component";
import { BannerCarouselComponent } from "src/app/components/banner-carousel/banner-carousel.component";
import { DropdownMobileFilterComponent } from "src/app/components/dropdown-mobile-filter/dropdown-mobile-filter.component";
import { EmptyStateInputComponent } from "src/app/components/empty-state-input/empty-state-input.component";
import { FiltersComponent } from "src/app/components/filters/filters.component";
import { GlobalFiltersService } from 'src/app/components/filters/services/global-filters.service';
import { ListingLoaderComponent } from "src/app/components/listing-loader/listing-loader.component";
import { LoaderDotComponent } from "src/app/components/loader-dot/loader-dot.component";
import { ResetFiltersService } from 'src/app/components/product-filters/reset-filters.service';
import { SortDropdownComponent } from "src/app/components/sort-dropdown/sort-dropdown.component";
import { SUGGESTION_KEY } from 'src/app/core/enums/suggestion-search-key.enum';
import { ListParams } from 'src/app/core/models/list-params.model';
import { GoogleAnalyticsService } from 'src/app/core/services/analytics/google-analytics.service';
import { AnnouncementsService } from 'src/app/core/services/announcements/announcements.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection/device-detection.service';
import { GlobalScrollService } from 'src/app/core/services/global-scroll/global-scroll.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { SeoService } from 'src/app/core/services/seo/seo.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { PublishStatus } from 'src/app/home/models/home-content.model';
import { HomeContentType } from 'src/app/home/models/home.model';
import { MappedProductListingSearch } from '../../models/product-listing.model';
import { ProductsPaginationService } from '../../services/products-pagination.service';
import { ProductsRepositoryService } from '../../services/products-repository.service';
import { BannerLayoutsComponent } from "../banner-layouts/banner-layouts.component";
import { FilterChipComponent } from "../filter-chip/filter-chip.component";
import { ProductsComponent } from "../products/products.component";

export const PAGE_SIZE = 21;

export interface Filters<T = any> {
  filters: T;
  refresh: boolean;
}

export const SORT_BY = [
  { value: '', viewValue: 'Sort By' },
  { value: 'created_at', viewValue: 'Newest' },
  { value: '-selling_price', viewValue: 'High Price' },
  { value: 'selling_price', viewValue: 'Low Price' },
];

export interface BreadCrumb {
  name: string,
  id: string,
  url: string
}
@Component({
  standalone: true,
  selector: 'webstore-products-layout',
  templateUrl: './products-layout.component.html',
  styleUrls: ['./products-layout.component.scss'],
  imports: [CommonModule, RouterModule, FilterChipComponent, ProductsComponent, ListingLoaderComponent, AnnouncementBarComponent, LoaderDotComponent, BannerCarouselComponent, BannerLayoutsComponent, SortDropdownComponent, DropdownMobileFilterComponent, FiltersComponent, EmptyStateInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { ngSkipHydration: 'true' }
})
export class ProductsLayoutComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  private productsRepositoryService = inject(ProductsRepositoryService);
  globalFiltersService = inject(GlobalFiltersService);
  private globalScrollService = inject(GlobalScrollService);
  private productsPaginationService = inject(ProductsPaginationService);
  private seoService = inject(SeoService);
  private resetFiltersService = inject(ResetFiltersService);
  private platformService = inject(PlatformService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);
  storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);
  announcementsService = inject(AnnouncementsService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private conversionTrackingService = inject(ConversionTrackingService);
  private cdr = inject(ChangeDetectorRef);

  // Expose device detection service signals for template usage
  get isDesktop() {
    return this.deviceDetectionService.isDesktop;
  }
  get isMobile() {
    return this.deviceDetectionService.isMobile;
  }
  get isTablet() {
    return this.deviceDetectionService.isTablet;
  }
  get deviceType() {
    return this.deviceDetectionService.deviceType;
  }

  @ViewChild(FiltersComponent) filtersComponent!: FiltersComponent;
  @ViewChild(FiltersComponent, { read: ElementRef }) filtersComponentElementRef!: ElementRef<HTMLElement>;

  // Subscription for internal sortBy control (when no parent provides it)
  private sortBySubscription?: any;

  private _filters = signal<Filters | null>(null);
  readonly filters = this._filters.asReadonly();

  private _productSearch = signal<MappedProductListingSearch>({
    _scroll_id: "",
    filter: [],
    hits: {
      hits: [],
      max_score: 0,
      total: {
        value: 0,
        relation: 'eq'
      },
      _scroll_id: ""
    }
  });
  readonly productSearch = this._productSearch.asReadonly();

  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();


  private _listLoading = signal(false);
  readonly listLoading = this._listLoading.asReadonly();

  private _pageNumber = signal(0);
  readonly pageNumber = this._pageNumber.asReadonly();

  private _listExhausted = signal(false);
  readonly listExhausted = this._listExhausted.asReadonly();

  private _brandName = signal<string>('');
  readonly brandName = this._brandName.asReadonly();

  private _collectionTitle = signal<string>('');
  readonly collectionTitle = this._collectionTitle.asReadonly();

  private _mediaQuery = signal(false);
  readonly mediaQuery = this._mediaQuery.asReadonly();

  // Computed signals for derived state
  readonly hasFilters = computed(() => !!this._filters());
  readonly hasProductSearch = computed(() => !!this._productSearch()?.hits?.hits?.length);
  readonly productCount = computed(() => this._productSearch()?.hits?.total?.value ?? 0);
  readonly isListExhausted = computed(() => this._listExhausted());
  readonly canLoadMore = computed(() => !this._listExhausted() && this._loading() === false);

  // Optional inputs from parent component (CategoryLayoutComponent)
  // When provided, these take precedence over route-based logic
  readonly breadcrumbData = input<BreadCrumb[]>([]);
  readonly selectedCategoryId = input<string>('');
  
  // Sort control from parent - if provided, use it instead of internal one
  readonly sortByControl = input<FormControl<string | null> | null>(null);
  readonly sortByOptionsInput = input<{ value: string; viewValue: string; }[]>([]);
  
  // Products data from parent - if provided, use it instead of fetching internally
  readonly productsInput = input<MappedProductListingSearch | null>(null);

  // Internal breadcrumb data (used when no parent input provided)
  private _breadcrumbData = signal<BreadCrumb[]>([]);

  // Computed breadcrumb data: prefer parent input, fallback to internal
  readonly breadcrumbDataComputed = computed(() => {
    const parentData = this.breadcrumbData();
    const internalData = this._breadcrumbData();
    return parentData.length > 0 ? parentData : internalData;
  });

  aggregations = this.globalFiltersService.aggregations;

  readonly sortByOptions = signal<{ value: string; viewValue: string; }[]>(SORT_BY);
  
  // Use parent's sortBy control if provided, otherwise create internal one
  private _internalSortBy = new FormControl<string | null>('');
  
  get sortBy(): FormControl<string | null> {
    return this.sortByControl() || this._internalSortBy;
  }

  get sortByValue(): { value: string; viewValue: string; }[] {
    const parentOptions = this.sortByOptionsInput();
    return parentOptions.length > 0 ? parentOptions : this.sortByOptions();
  }

  get aggregationsValue(): any[] {
    return this.aggregations() || [];
  }

  get productsValue(): any {
    return this.productSearch() ? null : null;
  }

  PublishStatus = PublishStatus
  HomeContentType = HomeContentType;

  filterShow = false;
  categoryName: string = '';

  // Computed selectedCategoryId: prefer parent input, fallback to internal
  readonly selectedCategoryIdComputed = computed(() => {
    const parentId = this.selectedCategoryId();
    return parentId || this._selectedCategoryId();
  });

  private _selectedCategoryId = signal<string>('');

  ssrLoader = true;
  isBrowser = false;

  readonly currentAppliedFilters = computed(() => this.globalFiltersService.currentAppliedFilters());

  salesChannelId = this.storeConfigurationRepositoryService.salesChannelId;
  showOutOfStockProducts = this.storeConfigurationRepositoryService.showOutOfStockProducts;

  announcement: Announcement | null = null;

  categoryBanners: CategorySection[] = [];

  sectionConfiguration: { show_product_list: boolean; show_filters: boolean; } = {
    show_product_list: true,
    show_filters: true
  }

  SUGGESTION_KEY = SUGGESTION_KEY;

  get categoryWebTopBanner() {
    return this.categoryBanners?.[0]?.banners?.find(banner => banner.type === 'web' && banner.position === 'top')?.banner_image_url;
  }
  get categoryWebLeftBanner() {
    return this.categoryBanners?.[0]?.banners?.find(banner => banner.type === 'web' && banner.position === 'left')?.banner_image_url;
  }
  get categoryMobileTopBanner() {
    return this.categoryBanners?.[0]?.banners?.find(banner => banner.type === 'mobile' && banner.position === 'top')?.banner_image_url;
  }

  constructor() {
    this.isBrowser = this.platformService.isBrowser();

    if (this.isBrowser) {
      this.ssrLoader = false;
    }

    // Initialize effects in constructor to ensure they're within injection context
    this.initializeEffects();

    // Watch for products input from parent (CategoryLayoutComponent)
    effect(() => {
      const parentProducts = this.productsInput();
      if (parentProducts) {
        // Use update to ensure signal change is detected properly
        this._productSearch.update(() => {
          // Create a deep copy to ensure change detection triggers
          return {
            ...parentProducts,
            hits: {
              ...parentProducts.hits,
              hits: [...(parentProducts.hits?.hits || [])]
            }
          };
        });
        if (parentProducts.filter) {
          this.globalFiltersService.updateAggregations(parentProducts.filter);
        }
        // Manually trigger change detection for OnPush strategy
        // Use setTimeout to ensure it runs after the current execution context
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 0);
      }
    });

    // Watch repository for data changes (from CategoryLayoutComponent) - only if no parent input
    effect(() => {
      const parentProducts = this.productsInput();
      // Skip if parent is providing products
      if (parentProducts) {
        return;
      }
      
      const repoProducts = this.productsRepositoryService.productsV2();
      const currentProductSearch = this._productSearch();

      // If repository has data but component signal is empty, sync it
      if (repoProducts?.hits?.hits?.length && (!currentProductSearch?.hits?.hits?.length || currentProductSearch.hits.hits.length === 0)) {
        this._productSearch.set(repoProducts);
        if (repoProducts.filter) {
          this.globalFiltersService.updateAggregations(repoProducts.filter);
        }
      }
    });
  }

  private initializeEffects(): void {
    // Service error handling effect
    effect(() => {
      const serviceError = this.productsRepositoryService.serviceError();
      if (serviceError) {
        // Products service error occurred
      }
    });

    effect(() => {
      const parentUrl = this.route.snapshot.parent?.url?.[0]?.path || '';
      const urlPath = this.route.snapshot.params?.url_path;

      // Handle fallback case when used within CategoryLayoutComponent
      if (!parentUrl && urlPath) {
        const filters = {
          // If showOutOfStockProducts value is false then in_stock set to true
          ...(this.showOutOfStockProducts() === false && {
            in_stock: true
          }),
          ...(urlPath && {
            category_url_paths: urlPath
          })
        };

        // Set the filters and trigger product loading
        this.setFilters({ filters, refresh: true });
      }
    });


    // This prevents circular dependencies and infinite loops
    const processedFilters = computed(() => {
      const filters = this.globalFiltersService.currentAppliedFilters();
      if (!filters || filters.length === 0) return null;

      // Process filters reactively without side effects
      const mappedCategory = filters?.map(categoryFilter => {
        if (categoryFilter.type === ProductFilterType.Category) {
          return categoryFilter?.filter.value;
        }
      }).filter(Boolean).join(',');

      const attributesFilter = filters?.map(attributeFilter => {
        if (attributeFilter.type === ProductFilterType.Attribute) {
          return ({ [`${attributeFilter.label}`]: attributeFilter?.filter?.value });
        }
      }).filter(Boolean);

      const priceFilter = filters?.map(priceFilter => {
        if (priceFilter.type === ProductFilterType.Price) {
          return ({ lte: priceFilter?.filter?.price_range?.to, gte: priceFilter?.filter?.price_range?.from })
        }
      }).filter(Boolean);

      const brandsFilter = filters?.map(attributeFilter => {
        if (attributeFilter.type === ProductFilterType.Brand) {
          return attributeFilter?.filter?.value;
        }
      }).filter(Boolean).join(',');

      return {
        categories: mappedCategory,
        attributes: JSON.stringify(attributesFilter),
        selling_price: JSON.stringify(priceFilter),
        brands: brandsFilter
      };
    });

    const shouldLoadProducts = computed(() => {
      const filters = this._filters();
      if (!filters || !filters.filters) return false;

      // Check if filters have meaningful content
      const hasValidFilters = Object.keys(filters.filters).some(key =>
        filters.filters[key] && filters.filters[key] !== '' && filters.filters[key] !== '[]'
      );

      return hasValidFilters || filters.refresh;
    });


    // Watch for device detection changes
    effect(() => {
      const isDesktop = this.deviceDetectionService.isDesktop();
      const isMobile = !isDesktop;
      // Update mediaQuery signal: true for desktop, false for mobile/tablet
      this._mediaQuery.set(isDesktop);
      this.updateComponentForDevice(isDesktop, isMobile);
    });
  }

  ngOnInit(): void {
    // First, check if repository already has data (from CategoryLayoutComponent)
    const repoProducts = this.productsRepositoryService.productsV2();

    if (repoProducts?.hits?.hits?.length) {
      // Found existing data in repository, sync to component signal
      this._productSearch.set(repoProducts);

      // Also update aggregations if available
      if (repoProducts.filter) {
        this.globalFiltersService.updateAggregations(repoProducts.filter);
      }
    }

    // Subscribe to internal sortBy control if parent didn't provide one
    // Parent will handle subscription if sortByControl is provided
    if (!this.sortByControl()) {
      this.sortBySubscription = this._internalSortBy.valueChanges.subscribe(ordering => {
        const filters: any = {
          ...(this._filters()?.filters || {}),
          ordering
        };
        this.setFilters({ filters, refresh: false });
      });
    }

    this.initializeComponent();
  }

  private initializeComponent(): void {

    this.announcement = this.announcementsService.getAnnouncement(AnnouncementBarPage.ProductListingPage);

    effect(async () => {
      // Skip initialization if parent is providing products
      if (this.productsInput()) {
        return;
      }
      
      let filter_type = this.route.snapshot.parent?.url?.[0]?.path || '';
      let filters = {};

      // Handle fallback case when used within CategoryLayoutComponent
      if (!filter_type && this.route.snapshot.params?.url_path) {
        filter_type = 'category';
      }

      switch (filter_type) {

        case 'search':

          const { q: query = '', tags = '', brand_id = '', brand_name = '', collection = null, ...additionalQueryParams } = this.route.snapshot.queryParams;

          const _collection = collection ? JSON.parse(collection) : null;

          const searchQuery = tags || brand_name || brand_id || _collection || additionalQueryParams?.display_name_keyword || additionalQueryParams?.seo_detail_keyword || additionalQueryParams?.category_name_keyword || additionalQueryParams?.collection_ids || query;
          this.conversionTrackingService.trackSearchConversion(searchQuery);

          filters = {
            ...(query && { search: query }),
            ...(this.showOutOfStockProducts() === false && {
              in_stock: true
            }),
            ...(tags && { tags }),
            ...(brand_id && {
              brand_ids: brand_id
            }),
            ...(!!collection && {
              collection_ids: _collection?.id
            }),
            ...additionalQueryParams
          };


          this.sortBy.patchValue('', { emitEvent: false });

          if (brand_name) {
            this._brandName.set(brand_name);
          }
          if (_collection) {
            this._collectionTitle.set(_collection?.name);
          }
          break;

        case 'category':
          const category_url_paths = this.route.snapshot.params?.url_path;
          filters = {
            // If showOutOfStockProducts value is false then in_stock set to true
            ...(this.showOutOfStockProducts() === false && {
              in_stock: true
            }),
            ...((category_url_paths && category_url_paths !== '') && {
              category_url_paths
            })
          };

          await this.productsRepositoryService.getProductCategory(category_url_paths);
          const category = this.productsRepositoryService.category();
          if (category) {
            this.seoService.updateSeoInformationForCategory(category);
            this.seoService.updateTitle(category?.name);
            // If parent didn't provide breadcrumb data, derive it in child
            if (this.breadcrumbData().length === 0) {
              this.getSelectedCategoryBreadCrumb(category?.id);
            }

            // Set selectedCategoryId if not provided by parent
            if (!this.selectedCategoryId()) {
              this._selectedCategoryId.set(category.id);
            }
            const sections = category?.category_banners?.find((banner: any) => banner.sales_channel?.id === this.salesChannelId);
            this.categoryBanners = sections?.sections?.slice()?.sort((section1: any, section2: any) => section1.position - section2.position) || [];
            if (sections?.configurations) {
              this.sectionConfiguration = sections?.configurations;
            } else {
              this.sectionConfiguration = {
                show_filters: true,
                show_product_list: true
              };
            }
          }
          // this.cdr.detectChanges();

          this.categoryName = category?.name || '';
          break;

        default:
          break;
      }

      // Skip if parent is providing products
      if (this.productsInput()) {
        return;
      }
      
      this.globalFiltersService.clearFilters();
      if (filters) {
        this.setFilters({ filters, refresh: true });
      }
    });

    effect(() => {
      const queryParams = this.route.snapshot.queryParams;
      let filters: any = {};
      const _queryParams = Object.keys(queryParams);
      _queryParams.forEach(key => {
        if (key === 'q') {
          filters.search = queryParams[key];
          // If showOutOfStockProducts value is false then in_stock set to true
          if (this.showOutOfStockProducts() === false) {
            filters.in_stock = true;
          }
        } else if (key === 'tags') {
          filters.tags = queryParams[key];
        } else {
          filters[key] = queryParams[key];
        }
      });


      // remove existed suggestion key 
      if (filters.hasOwnProperty(SUGGESTION_KEY.category_name_keywords)) {
        const currentFilters = this._filters();
        if (currentFilters?.filters) {
          delete currentFilters.filters[SUGGESTION_KEY.display_name_keywords];
          delete currentFilters.filters[SUGGESTION_KEY.seo_detail_keywords];
        }
      } else if (filters.hasOwnProperty(SUGGESTION_KEY.display_name_keywords)) {
        const currentFilters = this._filters();
        if (currentFilters?.filters) {
          delete currentFilters.filters[SUGGESTION_KEY.category_name_keywords];
          delete currentFilters.filters[SUGGESTION_KEY.seo_detail_keywords];
        }
      } else if (filters.hasOwnProperty(SUGGESTION_KEY.seo_detail_keywords)) {
        const currentFilters = this._filters();
        if (currentFilters?.filters) {
          delete currentFilters.filters[SUGGESTION_KEY.category_name_keywords];
          delete currentFilters.filters[SUGGESTION_KEY.display_name_keywords];
        }
      } else if (filters?.search) {
        const currentFilters = this._filters();
        if (currentFilters?.filters) {
          delete currentFilters.filters[SUGGESTION_KEY.display_name_keywords];
          delete currentFilters.filters[SUGGESTION_KEY.seo_detail_keywords];
          delete currentFilters.filters[SUGGESTION_KEY.category_name_keywords];
        }
      }

      filters = {
        ...(this._filters()?.filters || {}),
        ...filters,
      };

      // parse params
      const { brand_id = '', brand_name = '', collection = null, ...additionalQueryParams } = queryParams || {};

      const _collection = collection ? JSON.parse(collection) : null;
      const searchQuery = brand_name || brand_id || _collection || filters?.tags || filters?.search || filters?.display_name_keyword || filters?.seo_detail_keyword || filters?.category_name_keyword || '';
      this.conversionTrackingService.trackSearchConversion(searchQuery);

      if (brand_name) {
        this._brandName.set(brand_name);
      }
      if (_collection) {
        this._collectionTitle.set(_collection?.name);
      }

      filters = {
        ...filters,
        ...(brand_id && {
          brand_ids: brand_id
        }),
        ...(!!collection && {
          collection_ids: _collection?.id
        })
      };

      // Skip if parent is providing products
      if (this.productsInput()) {
        return;
      }
      
      this.setFilters({ filters, refresh: true });
    });

    // Note: SortBy changes are handled via subscription in ngOnInit
    // If parent provides sortByControl, parent handles the subscription
    // If no parent, child subscribes to _internalSortBy.valueChanges


    // Check store configuration readiness
    effect(() => {
      const storeConfig = this.storeConfigurationRepositoryService.storeConfigurations();
      const isStoreReady = this.storeConfigurationRepositoryService.isStoreConfigurationReady();
      // Store configuration check
    });

    effect(async () => {
      // Check if list is exhausted
      if (this._listExhausted()) {
        return;
      }

      const pageNumber = this._pageNumber();
      if (isNaN(pageNumber)) {
        return;
      }

      // Prevent automatic pagination on initial load or route change
      // This effect should only trigger for pagination (infinite scroll), not initial load
      // Check if we have existing products - if not, this is initial load, skip
      const existingProducts = this._productSearch()?.hits?.hits ?? [];
      
      // Skip if:
      // 1. pageNumber is 0 and we have no products (initial load)
      // 2. pageNumber is 0 and we're currently loading (route change/reset)
      if (pageNumber === 0) {
        if (existingProducts.length === 0 || this._loading()) {
          return; // Initial load or reset, let loadProducts handle it
        }
      }

      const nextPage = pageNumber + 1;
      this._pageNumber.set(nextPage);
      this.showListLoader();

      const currentFilters = this._filters();
      if (!currentFilters?.filters) {
        return null;
      }

      const params = new ListParams('', '', '', '', nextPage, PAGE_SIZE, '', {
        ...currentFilters.filters
      });


      // Use service state management pattern - service handles its own errors
      await this.productsRepositoryService.getProductsV2(params);

      // Check for service errors after operation
      const serviceError = this.productsRepositoryService.serviceError();
      if (serviceError) {
        return null;
      }

      // Handle the response
      const response = await this.productsRepositoryService.productsV2();
      this.hideListLoader();
      if (response) {
        const currentProducts = this._productSearch()?.hits?.hits ?? [];
        const fetchedProducts = response.hits.hits;
        const combinedListProducts = currentProducts.concat(fetchedProducts);
        const currentProductSearch = { ...this._productSearch() };
        currentProductSearch.hits.hits = combinedListProducts;
        this._productSearch.set(currentProductSearch);

        if (fetchedProducts.length === 0) {
          this._listExhausted.set(true);
        } else {
          this._listExhausted.set(false);
        }
      }
    });
  }

  getSelectedCategoryBreadCrumb(categoryId: string) {
    // Only set internal selectedCategoryId if not provided by parent
    if (!this.selectedCategoryId()) {
      this._selectedCategoryId.set(categoryId);
    }

    const findCategoryPath = (categories: any[], categoryId: string): any[] | null => {
      for (const category of categories) {
        // If the category is found by id, return the breadcrumb
        if (category.id === categoryId) {
          return [{ name: category.name, id: category.id, url: category.url_path }];
        }

        // If there are sub-categories, recursively search them
        if (category.sub_categories && category.sub_categories.length > 0) {
          const subCategoryPath = findCategoryPath(category.sub_categories, categoryId);
          if (subCategoryPath) {
            return [{ name: category.name, id: category.id, url: category.url_path }, ...subCategoryPath];
          }
        }
      }
      return null;
    };

    // Only derive breadcrumb if parent didn't provide it
    if (this.breadcrumbData().length > 0) {
      return;
    }

    const bodyConfigurations = this.storeConfigurationRepositoryService.bodyConfigurations();

    if (bodyConfigurations?.categories) {
      // Find the full path for the given categoryId
      const categoryPath = findCategoryPath(bodyConfigurations.categories, categoryId);

      // Update internal breadcrumb data signal
      const breadcrumbData = categoryPath ? [...categoryPath] : [];
      this._breadcrumbData.set(breadcrumbData);
    }
  }


  removeFilter(filter: FilterValue) {
    this.filtersComponent.updateFilter(filter);
  }

  async loadProducts(filtersApplied?: Filters) {
    const { filters, refresh = false } = filtersApplied || {};


    const params = new ListParams('', '', '', '', 0, PAGE_SIZE, '', filters);
    this.showLoader();


    // Use service state management pattern - no try-catch needed
    await this.productsRepositoryService.getProductsV2(params);
    const response = this.productsRepositoryService.productsV2();

    // Check for service errors after operation
    const serviceError = this.productsRepositoryService.serviceError();
    if (serviceError) {
      this.hideLoader();
      return;
    }

    if (refresh && response?.filter) {
      this.globalFiltersService.updateAggregations(response.filter);
    }

    this._listExhausted.set(false);
    this._pageNumber.set(0);
    this.hideLoader();

    if (response) {
      this._productSearch.set(response);
      this.productsPaginationService.scrollId = response._scroll_id || '';
    }
  }

  private updateComponentForDevice(isDesktop: boolean, isMobile: boolean): void {
    if (isMobile) {
      this.hideFilters();
    }
  }

  // This method now uses the reactive computed signal pattern
  applyFilters(filters: FilterSelection[]) {
    this.globalFiltersService.applyFilters(filters);
  }

  get isSearching(): boolean {
    const isSearching =
      (this.route.snapshot.parent?.url?.length || 0) > 0 &&
      this.route.snapshot.parent?.url?.[0]?.path === 'search' &&
      (this.route.snapshot.queryParams?.q !== '' ||
        this.route.snapshot.queryParams.category_name_keyword ||
        this.route.snapshot.queryParams.display_name_keyword ||
        this.route.snapshot.queryParams.seo_detail_keyword);

    return Boolean(isSearching);
  }

  get isViewingProducts(): boolean {
    const isViewingProducts =
      (this.route.snapshot.parent?.url?.length || 0) > 0 &&
      this.route.snapshot.parent?.url?.[0]?.path === 'products';

    return Boolean(isViewingProducts);
  }

  get isSearchingBrandProducts(): boolean {
    const isSearchingBrandProducts =
      (this.route.snapshot.parent?.url?.length || 0) > 0 &&
      this.route.snapshot.parent?.url?.[0]?.path === 'search' &&
      !!this.route.snapshot.parent?.queryParamMap?.get('brand_id') &&
      !!this.route.snapshot.parent?.queryParamMap?.get('brand_name');
    return Boolean(isSearchingBrandProducts);
  }

  get isViewingProductCollection(): boolean {
    const isViewingProductCollection =
      (this.route.snapshot.parent?.url?.length || 0) > 0 &&
      this.route.snapshot.parent?.url?.[0]?.path === 'search' &&
      !!this.route.snapshot.parent?.queryParamMap?.get('collection');
    return Boolean(isViewingProductCollection);
  }

  setFilters(filters: Filters<any>) {
    this._filters.set(filters);
    this._pageNumber.set(0);

    // Skip loading if parent is providing products (parent will handle API calls)
    if (this.productsInput()) {
      return;
    }

    // This is the proper way instead of using effects for side effects
    this.loadProducts(filters);
  }

  showFilters() {
    this.filterShow = true;
    this.filtersComponentElementRef?.nativeElement.classList.add('slide-in');
    this.filtersComponentElementRef?.nativeElement.classList.remove('slide-out');
  }

  hideFilters() {
    this.filterShow = false;
    this.filtersComponentElementRef?.nativeElement.classList.add('slide-out');
    this.filtersComponentElementRef?.nativeElement.classList.remove('slide-in');
  }

  // DeviceDetectionService handles all device detection automatically
  private showLoader() {
    this._loading.set(true);
  }

  private hideLoader() {
    this._loading.set(false);
  }

  private showListLoader() {
    this._listLoading.set(true);
  }

  private hideListLoader() {
    this._listLoading.set(false);
  }

  ngOnDestroy() {
    this.conversionTrackingService.removeConversionTracking('Search');
    
    // Clean up sortBy subscription if it exists
    if (this.sortBySubscription) {
      this.sortBySubscription.unsubscribe();
    }
  }

}
